import m from "mithril";
import CoordMobileLayout from "./Coord-mobile-layout.js";
import { getMithril, showToast } from "../utils.js";
import dataLayer from "../pwa/data-layer.js";

var CoordCustomersMobile = {
    oninit: function(vnode) {
        this.customers = [];
        this.isLoading = false;
        this.error = null;
        this.searchTerm = "";
        this.currentPage = 1;
        this.itemsPerPage = 20; // More items per page for mobile
        this.pagination = {
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false
        };

        this.fetchCustomers();
    },

    fetchCustomers: async function(page = 1) {
        try {
            this.currentPage = page;

            const params = new URLSearchParams({
                page: page.toString(),
                limit: this.itemsPerPage.toString()
            });

            if (this.searchTerm) {
                params.append('search', this.searchTerm);
            }

            // Use data layer for cache-first reads with background refresh
            this.customers = await dataLayer.getCustomers(Object.fromEntries(params));
            
            // Note: Pagination info would need to be stored in meta or handled differently
            // For now, we show all cached customers
            this.pagination = {
                total: this.customers.length,
                totalPages: Math.ceil(this.customers.length / this.itemsPerPage),
                hasNext: page < Math.ceil(this.customers.length / this.itemsPerPage),
                hasPrev: page > 1
            };
        } catch (error) {
            console.error("Error fetching customers:", error);
            this.error = error.message;
            showToast("Gagal memuat data nasabah: " + error.message, "error");
        }
    },

    search: function() {
        this.currentPage = 1;
        // For offline support, filter locally from cached data
        // Background refresh will update cache if online
        this.fetchCustomers(1);
    },

    // Get filtered customers based on search term (for offline support)
    getFilteredCustomers: function() {
        if (!this.searchTerm) {
            return this.customers;
        }
        
        const term = this.searchTerm.toLowerCase();
        return this.customers.filter(customer => 
            (customer.nama_nasabah && customer.nama_nasabah.toLowerCase().includes(term)) ||
            (customer.no_ktp && customer.no_ktp.includes(term)) ||
            (customer.no_reg && customer.no_reg.toLowerCase().includes(term)) ||
            (customer.telepon && customer.telepon.includes(term))
        );
    },

    goToPage: function(page) {
        if (page >= 1 && page <= this.pagination.totalPages) {
            this.fetchCustomers(page);
        }
    },

    formatCurrency: function(amount) {
        if (!amount && amount !== 0) return amount;
        const num = parseFloat(amount);
        if (isNaN(num)) return amount;
        return `Rp ${num.toLocaleString('id-ID')}`;
    },

    viewCustomerDetails: function(customerId) {
        showToast("Membuka detail nasabah...", "info");
        m.route.set(`/coord-view-customer-mobile/${customerId}`);
    },

    callCustomer: function(phoneNumber) {
        if (phoneNumber && phoneNumber !== "-") {
            window.location.href = `tel:${phoneNumber}`;
        } else {
            showToast("Nomor telepon tidak tersedia", "warning");
        }
    },

    navigateBack: function() {
        m.route.set("/coord-dashboard-mobile");
    },

    addCustomer: function() {
        m.route.set("/coord-add-customer-mobile");
    },

    view: function(vnode) {
        const content = m("div", { class: "min-h-screen bg-gray-50 dark:bg-gray-900" }, [
            // Search bar
            m("div", { class: "px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" }, [
                m("div", { class: "relative" }, [
                    m("input", {
                        type: "text",
                        placeholder: "Cari nama atau telepon...",
                        class: "w-full pl-4 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-base",
                        value: this.searchTerm,
                        oninput: (e) => {
                            this.searchTerm = e.target.value;
                        },
                        onkeypress: (e) => {
                            if (e.key === "Enter") {
                                this.search();
                            }
                        }
                    }),
                    m("button", {
                        class: "absolute right-3 top-1/2 transform -translate-y-1/2 p-1",
                        onclick: () => this.search()
                    }, [
                        m("i", { class: "ri-search-line text-xl text-gray-500 dark:text-gray-400" })
                    ])
                ])
            ]),

            // Error message
            this.error && !this.isLoading ? m("div", {
                class: "mx-4 my-4 bg-red-50 dark:bg-red-900 border-l-4 border-red-500 p-4 rounded"
            }, [
                m("div", { class: "flex items-center" }, [
                    m("i", { class: "ri-error-warning-line text-red-500 dark:text-red-300 text-xl mr-3" }),
                    m("div", [
                        m("p", { class: "text-red-700 dark:text-red-200 font-medium" }, "Gagal Memuat Data"),
                        m("p", { class: "text-red-600 dark:text-red-300 text-sm mt-1" }, this.error)
                    ])
                ]),
                m("button", {
                    class: "mt-3 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors duration-200",
                    onclick: () => this.fetchCustomers()
                }, "Coba Lagi")
            ]) : null,

            // Loading indicator
            this.isLoading ? m("div", {
                class: "flex justify-center items-center h-64"
            }, [
                m("div", { class: "text-center" }, [
                    m("i", { class: "ri-loader-4-line animate-spin text-4xl text-orange-500 mb-4 block" }),
                    m("p", { class: "text-gray-600 dark:text-gray-300" }, "Memuat data nasabah...")
                ])
            ]) : null,

            // Customer list
            !this.isLoading && !this.error ? m("div", { class: "px-4 py-4 space-y-3" }, [
                this.getFilteredCustomers().length === 0 ?
                    m("div", { class: "text-center py-12" }, [
                        m("i", { class: "ri-user-line text-4xl text-gray-400 dark:text-gray-500 mb-4 block" }),
                        m("p", { class: "text-gray-500 dark:text-gray-400" }, "Tidak ada data nasabah")
                    ]) :
                    this.getFilteredCustomers().map(customer => {
                        const isGroupCustomer = customer.customer_type === 'kelompok';

                        return m("div", {
                            class: `bg-white dark:bg-gray-800 rounded-lg shadow-sm border transition-all duration-200 ${
                                isGroupCustomer ? 'border-blue-200 dark:border-blue-800' : 'border-gray-200 dark:border-gray-700'
                            } hover:shadow-md active:scale-95`,
                            onclick: () => this.viewCustomerDetails(customer.id)
                        }, [
                            // Customer header
                            m("div", { class: "p-4 border-b border-gray-100 dark:border-gray-700" }, [
                                m("div", { class: "flex items-center justify-between mb-2" }, [
                                    m("div", { class: "flex items-center space-x-2" }, [
                                        isGroupCustomer ?
                                            m("i", { class: "ri-group-line text-blue-600 dark:text-blue-400" }) :
                                            m("i", { class: "ri-user-line text-gray-600 dark:text-gray-400" }),
                                        m("h3", {
                                            class: `font-semibold ${isGroupCustomer ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'}`
                                        }, customer.nama_nasabah),
                                        isGroupCustomer ? m("span", {
                                            class: "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200"
                                        }, "Kelompok") : null
                                    ]),
                                    m("button", {
                                        class: `p-2 rounded-full transition-colors ${
                                            customer.no_telepon && customer.no_telepon !== "-" ?
                                                "text-green-600 hover:bg-green-50 dark:hover:bg-green-900" :
                                                "text-gray-400 cursor-not-allowed"
                                        }`,
                                        onclick: (e) => {
                                            e.stopPropagation();
                                            this.callCustomer(customer.no_telepon);
                                        },
                                        disabled: !customer.no_telepon || customer.no_telepon === "-"
                                    }, [
                                        m("i", { class: "ri-phone-line text-lg" })
                                    ])
                                ]),

                                // Phone number
                                customer.no_telepon && customer.no_telepon !== "-" ? m("div", {
                                    class: "flex items-center text-sm text-gray-600 dark:text-gray-300 mb-2"
                                }, [
                                    m("i", { class: "ri-phone-line mr-2" }),
                                    m("span", customer.no_telepon)
                                ]) : null,

                                // Address
                                m("div", { class: "flex items-start text-sm text-gray-600 dark:text-gray-300" }, [
                                    m("i", { class: "ri-map-pin-line mr-2 mt-0.5" }),
                                    m("span", {
                                        class: "leading-relaxed"
                                    }, isGroupCustomer ?
                                        "Data kelompok tersedia di detail" :
                                        `${customer.alamat || ""}${customer.rt || customer.rw ? `, RT${customer.rt || ""}/RW${customer.rw || ""}` : ""}`
                                    )
                                ])
                            ]),

                            // Financial Balances (Portfolio Style)
                            m("div", { class: "px-4 pb-4" }, [
                                m("div", { class: "grid grid-cols-2 gap-4 border-t border-gray-50 dark:border-gray-700 pt-3" }, [
                                    m("div", [
                                        m("p", { class: "text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1" }, "Simpanan"),
                                        m("p", { class: "text-sm font-bold text-blue-600" }, this.formatCurrency(customer.total_savings_balance))
                                    ]),
                                    m("div", [
                                        m("p", { class: "text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1" }, "Pinjaman"),
                                        m("p", { class: "text-sm font-bold text-red-600" }, this.formatCurrency(customer.total_loan_outstanding))
                                    ])
                                ])
                            ]),

                            // Action hint
                            m("div", { class: "px-4 py-2 text-center bg-gray-50/50 dark:bg-gray-800/50 rounded-b-lg border-t border-gray-50 dark:border-gray-700" }, [
                                m("p", { class: "text-[10px] text-gray-400 dark:text-gray-500" }, "Ketuk untuk melihat detail")
                            ])
                        ]);
                    }),

                // Pagination (simplified for mobile)
                this.pagination.totalPages > 1 ? m("div", {
                    class: "flex justify-center items-center space-x-4 py-6"
                }, [
                    m("button", {
                        class: `flex items-center px-4 py-2 rounded-lg transition-colors ${
                            this.pagination.hasPrev ?
                                "bg-orange-600 hover:bg-orange-700 text-white" :
                                "bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                        }`,
                        disabled: !this.pagination.hasPrev,
                        onclick: () => this.goToPage(this.currentPage - 1)
                    }, [
                        m("i", { class: "ri-arrow-left-s-line mr-2" }),
                        "Sebelumnya"
                    ]),

                    m("span", {
                        class: "text-sm text-gray-600 dark:text-gray-300 font-medium"
                    }, `${this.currentPage} / ${this.pagination.totalPages}`),

                    m("button", {
                        class: `flex items-center px-4 py-2 rounded-lg transition-colors ${
                            this.pagination.hasNext ?
                                "bg-orange-600 hover:bg-orange-700 text-white" :
                                "bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                        }`,
                        disabled: !this.pagination.hasNext,
                        onclick: () => this.goToPage(this.currentPage + 1)
                    }, [
                        "Berikutnya",
                        m("i", { class: "ri-arrow-right-s-line ml-2" })
                    ])
                ]) : null
            ]) : null,

            // Floating add button
            m("div", { class: "fixed bottom-20 right-4 z-50" }, [
                m("button", {
                    class: "bg-orange-600 hover:bg-orange-700 text-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-200",
                    onclick: () => this.addCustomer(),
                    title: "Tambah Nasabah"
                }, [
                    m("i", { class: "ri-add-line text-lg" })
                ])
            ])
        ]);

        return m(CoordMobileLayout, { title: "Data Nasabah", activeSection: "nasabah" }, content);
    }
};

export default CoordCustomersMobile;